import { Link } from "react-router-dom";
import { Icon } from "../../components/Icon";
import {
  Button,
  Card,
  Pill,
  ProgressBar,
  SectionHeading,
} from "../../components/ui";
import { GUILDS } from "../../data/seed";
import { toStudentAnalytics } from "../../lib/progression";
import { usePortal } from "../../state/PortalContext";

export function FacultyHomePage() {
  const { cohort, curriculum, students, studentProgress } = usePortal();
  const courseRatio = getCourseRatio(cohort.startsAt, cohort.endsAt);
  const analytics = students.map((student) =>
    toStudentAnalytics(
      student,
      studentProgress[student.id],
      cohort.riskRules,
      courseRatio,
    ),
  );
  const atRisk = analytics.filter((student) => student.riskReasons.length > 0);
  const averageCompletion = Math.round(
    analytics.reduce((sum, item) => sum + item.completion, 0) /
      Math.max(1, analytics.length),
  );
  const liveQuests = curriculum.quests.filter(
    (quest) => quest.templateId !== "coming-soon",
  ).length;

  return (
    <div className="space-y-7">
      <SectionHeading
        eyebrow="Faculty Command Center"
        title="Course operations at a glance"
        detail={`${cohort.name} · ${cohort.term}. See the signals that need attention without turning the experience into a generic LMS.`}
        action={
          <Pill tone={curriculum.status === "published" ? "emerald" : "amber"}>
            Curriculum v{curriculum.version} · {curriculum.status}
          </Pill>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: "Enrolled adventurers",
            value: students.length,
            icon: "Users",
            tone: "text-cyan-300",
          },
          {
            label: "Average completion",
            value: `${averageCompletion}%`,
            icon: "Compass",
            tone: "text-indigo-300",
          },
          {
            label: "Students needing attention",
            value: atRisk.length,
            icon: "ShieldAlert",
            tone: "text-rose-300",
          },
          {
            label: "Playable templates",
            value: liveQuests,
            icon: "Gamepad2",
            tone: "text-emerald-300",
          },
        ].map((stat) => (
          <Card key={stat.label} className="p-5">
            <div className="flex items-center justify-between">
              <span className={`rounded-xl bg-slate-950 p-3 ${stat.tone}`}>
                <Icon name={stat.icon} size={23} />
              </span>
              <span className="font-display text-3xl font-bold text-white">
                {stat.value}
              </span>
            </div>
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              {stat.label}
            </p>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_.85fr]">
        <Card className="p-7">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-retro text-[8px] tracking-widest text-rose-300">
                ATTENTION QUEUE
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-white">
                Transparent at-risk signals
              </h2>
            </div>
            <Link to="/faculty/analytics">
              <Button variant="secondary">Open analytics</Button>
            </Link>
          </div>
          <div className="mt-6 space-y-3">
            {atRisk.slice(0, 5).map((item) => (
              <div
                key={item.student.id}
                className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-950/45 p-4 sm:flex-row sm:items-center"
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-rose-400/10 text-rose-300">
                  <Icon name="ShieldAlert" size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-200">{item.student.fullName}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.riskReasons.join(" · ")}
                  </p>
                </div>
                <div className="w-full sm:w-32">
                  <ProgressBar value={item.completion} />
                  <p className="mt-1 text-right text-[9px] text-slate-600">
                    {item.completion}% complete
                  </p>
                </div>
              </div>
            ))}
            {atRisk.length === 0 && (
              <p className="rounded-2xl border border-emerald-400/15 bg-emerald-400/5 p-5 text-sm text-emerald-200">
                No students currently trigger the cohort's risk rules.
              </p>
            )}
          </div>
        </Card>

        <Card className="p-7">
          <p className="font-retro text-[8px] tracking-widest text-violet-300">
            GUILD DISTRIBUTION
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold text-white">
            Current class interests
          </h2>
          <div className="mt-6 space-y-4">
            {GUILDS.map((guild) => {
              const count = students.filter(
                (student) => student.guild === guild.id,
              ).length;
              return (
                <div key={guild.id}>
                  <div className="mb-1.5 flex justify-between text-xs">
                    <span className="font-bold text-slate-300">{guild.name}</span>
                    <span className="text-slate-500">{count}</span>
                  </div>
                  <ProgressBar
                    value={(count / Math.max(1, students.length)) * 100}
                    color=""
                  />
                  <div
                    className="-mt-2.5 h-2.5 rounded-full"
                    style={{
                      width: `${(count / Math.max(1, students.length)) * 100}%`,
                      backgroundColor: guild.color,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          {
            to: "/faculty/curriculum",
            icon: "Edit3",
            title: "Curriculum Forge",
            detail: "Edit the map, quests, retry rules, and puzzle parameters.",
          },
          {
            to: "/faculty/cohorts",
            icon: "UserPlus",
            title: "Cohorts & Rosters",
            detail: "Invite students and tune transparent risk thresholds.",
          },
          {
            to: "/faculty/analytics",
            icon: "BarChart3",
            title: "Learning Signals",
            detail: "Inspect mastery bottlenecks and export the gradebook.",
          },
        ].map((link) => (
          <Link key={link.to} to={link.to}>
            <Card className="h-full p-6 transition hover:-translate-y-1 hover:border-indigo-400/30">
              <Icon name={link.icon} size={27} className="text-indigo-300" />
              <h3 className="mt-5 font-display text-xl font-bold text-white">
                {link.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {link.detail}
              </p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function getCourseRatio(startsAt: string, endsAt: string) {
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  return Math.max(0, Math.min(1, (Date.now() - start) / (end - start)));
}
